import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Target, Users, UserPlus, ShieldCheck, FileSignature,
  Package, CheckSquare, BarChart3, FolderArchive, Settings, Search,
  Bell, LogOut, Menu, X, Clock, Gauge, CalendarClock, Sparkles, CalendarCheck, ClipboardCheck, ClipboardList, UserCog, Timer,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import logo from "@/assets/murphy-logo.png";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, key: "dashboard" },
  { to: "/leads", icon: Target, label: "Leads", key: "leads" },
  { to: "/kunder", icon: Users, label: "Kunder", key: "kunder" },
  { to: "/onboarding", icon: UserPlus, label: "Onboarding", key: "onboarding" },
  { to: "/kyc", icon: ShieldCheck, label: "KYC", key: "kyc" },
  { to: "/avtaler", icon: FileSignature, label: "Avtaler", key: "avtaler" },
  { to: "/produkter", icon: Package, label: "Produkter", key: "produkter" },
  { to: "/oppgaver", icon: CheckSquare, label: "Oppgaver", key: "oppgaver" },
  { to: "/assistent", icon: Sparkles, label: "Assistent", key: "assistent" },
  { to: "/timeforing", icon: Clock, label: "Timeføring", key: "timeforing" },
  { to: "/timer", icon: Timer, label: "Timer", key: "timer" },
  { to: "/ansatt", icon: UserCog, label: "Ansatt", key: "ansatt" },
  { to: "/ressursplan", icon: Gauge, label: "Ressursplan", key: "ressursplan" },
  { to: "/rapporter", icon: BarChart3, label: "Rapporter", key: "rapporter" },
  { to: "/arsregnskap", icon: CalendarCheck, label: "Årsregnskap", key: "arsregnskap" },
  { to: "/compliance", icon: CalendarClock, label: "Compliance", key: "compliance" },
  { to: "/kvalitetskontroll", icon: ClipboardCheck, label: "Kvalitetskontroll", key: "kvalitetskontroll" },
  { to: "/oppdragsutforelse", icon: ClipboardList, label: "Oppdragsutførelse", key: "oppdragsutforelse" },
  { to: "/dokumenter", icon: FolderArchive, label: "Dokumenter", key: "dokumenter" },
  { to: "/admin", icon: Settings, label: "Administrasjon", key: "admin" },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    if (q.length < 2) { setRes(null); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <Popover open={!!res && q.length >= 2}>
      <PopoverTrigger asChild>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Søk kunder, org.nr, personer, dokumenter…"
            className="pl-9 bg-secondary border-border h-10"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0 bg-popover border-border" align="start">
        <div className="max-h-96 overflow-y-auto">
          {res?.customers?.length > 0 && (
            <div className="p-2">
              <p className="label-caps px-2 py-1">Kunder</p>
              {res.customers.map((c) => (
                <button key={c.id} data-testid={`search-result-${c.id}`}
                  onClick={() => { nav(`/kunder/${c.id}`); setQ(""); setRes(null); }}
                  className="w-full text-left px-2 py-2 rounded hover:bg-accent text-sm flex justify-between">
                  <span>{c.legal_name}</span>
                  <span className="text-muted-foreground text-xs">{c.org_number}</span>
                </button>
              ))}
            </div>
          )}
          {res && !res.customers?.length && !res.documents?.length && !res.tasks?.length && (
            <p className="p-4 text-sm text-muted-foreground">Ingen treff</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  const unread = items.filter((n) => !n.read).length;
  const handleClick = async (n) => {
    const target = n.link || (n.customer_id ? `/kunder/${n.customer_id}` : null);
    if (!n.read) { try { await api.put(`/notifications/${n.id}/read`); } catch { /* ignore */ } }
    setOpen(false);
    load();
    if (target) nav(target);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-md hover:bg-accent transition-colors">
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-destructive text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-popover border-border" align="end">
        <div className="p-3 border-b border-border flex justify-between items-center">
          <span className="font-medium text-sm">Varsler</span>
          <button className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => api.put("/notifications/read-all").then(load)}>Merk alle lest</button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <p className="p-4 text-sm text-muted-foreground">Ingen varsler</p>}
          {items.map((n) => {
            const clickable = !!(n.link || n.customer_id);
            return (
              <button
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => handleClick(n)}
                className={`w-full text-left p-3 border-b border-border/50 text-sm transition-colors ${!n.read ? "bg-accent/40" : ""} ${clickable ? "hover:bg-accent cursor-pointer" : "cursor-default"}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium">{n.title}</p>
                  {clickable && <span className="text-[10px] text-muted-foreground shrink-0 mt-0.5">Åpne →</span>}
                </div>
                <p className="text-muted-foreground text-xs mt-0.5">{n.message}</p>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen noise-bg flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center px-6 border-b border-border">
          <img src={logo} alt="Murphy Control" className="h-7 w-auto brightness-0 invert" data-testid="sidebar-logo" />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.key}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-white text-black font-semibold" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}>
              <n.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{ROLE_LABELS[user?.role] || user?.role}</p>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border flex items-center gap-4 px-4 sm:px-6 bg-background/80 backdrop-blur sticky top-0 z-20">
          <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex-1"><GlobalSearch /></div>
          <NotificationBell />
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
